import { useMemo } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useDraftModeStore, selectIsDraftMode } from '@/stores/draftModeStore';
import { useParametersJson, useParametersDraftJson } from './useParameters';
import type { ParametersFile } from '@/types/logicApps';

/**
 * Fetches the selected app's parameters file so consumers can surface
 * `@parameters('name')` tokens without each one duplicating the app-context
 * and react-query plumbing. Honors draft mode: when the designer is in draft
 * mode, returns `parameters-draft.json` so token previews and expression
 * evaluation use the in-progress values rather than the published file.
 * Returns `undefined` until an app is selected and the file has loaded.
 */
export function useAppLevelParameters(): ParametersFile | undefined {
  const selectedApp = useAppStore((s) => s.selectedApp);
  const selectedProject = useAppStore((s) => s.selectedProject);
  const isDraftMode = useDraftModeStore(selectIsDraftMode);

  const info = useMemo(() => {
    if (!selectedApp || !selectedProject) return null;
    return {
      projectName: selectedProject,
      appName: selectedApp.name,
      appId: selectedApp.properties.appResourceId,
    };
  }, [selectedApp, selectedProject]);

  const { data: prodData } = useParametersJson(isDraftMode ? null : info);
  const { data: draftData } = useParametersDraftJson(isDraftMode ? info : null);
  return isDraftMode ? draftData : prodData;
}
